import React, { useState, useEffect, useCallback } from "react";
import { Auth } from "aws-amplify";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useLoading } from "../../../context/LoadingContext";
import "../../../styles/general.css";
import "../../../styles/amtpl.css";
import "../../../styles/layout.css";
import DataTable from "../../../pages/Requirement/components/common/ReusableTable";
import SearchFilter from "../../../pages/Requirement/components/common/SearchFilter";
import {
  fetchData,
  postQuery,
} from "../../../pages/Requirement/services/RequirementServices";
import AlertModal from "../../Requirement/components/common/AlerModel";
import dayjs from "dayjs";

const UnapprovedProduce = () => {
  const navigate = useNavigate();
  const { setLoading, loading } = useLoading();

  const [activeTab, setActiveTab] = useState("commodityDetails");
  const [showAddForm, setShowAddForm] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [sellerItemDetails, setSellerItemDetails] = useState([]);
  const [selectedSellerItemDetail, setSelectedSellerItemDetail] =
    useState(null);
  const [currentSellerItemDetailPage, setCurrentSellerItemDetailPage] =
    useState(1);
  const [totalSellerItemDetailPages, setTotalSellerItemDetailPages] =
    useState(1);
  const [selectedProductName, setSelectedProductName] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");
  const [products, setProducts] = useState([]);
  const [states, setStates] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [formData, setFormData] = useState({});
  const [filterTerms, setFilterTerms] = useState({
    product: "",
    grade: "",
    state: "",
    approved: "No",
    ExpiresGT: `'${dayjs().format("YYYY-MM-DD")}'`,
  });
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };
  const pageSize = 20;

  const fields = [
    {
      name: "product",
      label: "Product",
      type: "dropdown",
      options: [
        { value: "", label: "Select a product" },
        ...[...new Set(products.map((product) => product.ProductName))].map(
          (product) => ({ value: product, label: product })
        ),
      ],
      value: filterTerms.product,
      onChange: (value) => {
        setFilterTerms((prev) => ({ ...prev, product: value, grade: "" }));
      },
    },
    {
      name: "grade",
      label: "Grade",
      type: "dropdown",
      options: [
        { value: "", label: "Select a Grade" },
        ...products
          .filter((product) => product.ProductName === filterTerms.product)
          .map((product) => ({ value: product.Grade, label: product.Grade })),
      ],
      value: filterTerms.grade,
      onChange: (value) => {
        setFilterTerms((prev) => ({ ...prev, grade: value }));
      },
      disabled: !filterTerms.product,
    },
    {
      name: "state",
      label: "State",
      type: "dropdown",
      options: [
        { value: "", label: "Select State" },
        ...states.map((state) => ({ value: state, label: state })),
      ],
      value: filterTerms.state,
      onChange: (value) => {
        setFilterTerms((prev) => ({ ...prev, state: value }));
      },
    },
  ];

  const columns = [
    { id: "ProductName", numeric: false, label: "Product" },
    { id: "Grade", numeric: false, label: "Grade" },
    { id: "Quantity", numeric: true, label: "Quantity" },
    { id: "Price", numeric: true, label: "Price" },
    { id: "State", numeric: false, label: "State" },
    {
      id: "CreatedAt",
      numeric: false,
      label: "Created On",
      renderCell: (row) =>
        row.CreatedAt ? dayjs(row.CreatedAt).format("DD/MM/YYYY") : "",
    },
    {
      id: "Expires",
      numeric: false,
      label: "Expires On",
      renderCell: (row) =>
        row.Expires ? dayjs(row.Expires).format("DD/MM/YYYY") : "",
    },
    { id: "Approved", numeric: false, label: "Status" },
  ];

  const handleRowClick = useCallback((row) => {
    setSelectedSellerItemDetail(row);
    setSelectedProductName(row.ProductName);
    setSelectedGrade(row.Grade);
    setSelectedPaymentOption(row.Payment || "");
    setSelectedState(row.State || "");

    // Format Expires date to YYYY-MM-DD format for date input
    let expiresDate = "";
    if (row.Expires) {
      // Extract just the date part (YYYY-MM-DD) from datetime string
      expiresDate = row.Expires.split(" ")[0];
    }

    // Populate form with selected row data
    setFormData({
      ProductID: row.ProductID || "",
      Quantity: row.Quantity || "",
      PricePerMT: row.Price || "",
      State: row.State || "",
      Country: row.Country || "India",
      Expires: expiresDate,
      Currency: row.Currency || "INR",
      TotalPrice: row.TotalPrice || "",
      Payment: row.Payment || "",
      HsCode: row.HsCode || "",
      Variety: row.Variety || "",
      productSpecifications: row.productSpecifications || "",
    });

    setIsEditMode(true);
    setShowAddForm(true);
    setActiveTab("commodityDetails");
  }, []);

  const handlePageChange = useCallback((page) => {
    setCurrentSellerItemDetailPage(page);
  }, []);

  const currentPage = currentSellerItemDetailPage;

  const fetchSellerItemDetails = useCallback(async () => {
    setLoading(true);
    try {
      const body = {
        target: "listSellerItemDetails",
        UserOrder: "CreatedAt DESC",
        filterTerms: filterTerms,
        rowStart: (currentSellerItemDetailPage - 1) * pageSize,
        pageSize: pageSize,
      };

      const result = await fetchData(body);
      if (result) {
        result.items = result.items.map((item) => ({
          ...item,
          id: item.SellerItemID,
        }));
        setSellerItemDetails(result.items || []);
        const totalItems = result.total || 0;
        setTotalSellerItemDetailPages(Math.ceil(totalItems / pageSize));

        if (result.items && result.items.length > 0) {
          const firstItem = result.items[0];
          setSelectedSellerItemDetail(firstItem);
          setSelectedProductName(firstItem.ProductName);
          setSelectedGrade(firstItem.Grade);
          setSelectedPaymentOption(firstItem.Payment);
          setSelectedState(firstItem.State);
        } else {
          setSelectedSellerItemDetail(null);
          setSelectedProductName("");
          setSelectedGrade("");
          setShowAddForm(false); // Hide form when no data
          setIsEditMode(false);
        }
      }
    } catch (error) {
      console.error("Error fetching seller item details:", error);
    } finally {
      setLoading(false);
    }
  }, [currentSellerItemDetailPage, filterTerms, setLoading]);

  const fetchProducts = useCallback(async () => {
    try {
      const body = { target: "listProducts", rowStart: 0, pageSize: 1000 };
      const result = await fetchData(body);
      if (result) setProducts(result.items || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  }, []);

  const fetchStates = useCallback(async () => {
    try {
      const body = {
        target: "listDropDownOptions",
        filterTerms: { Type: "State" },
        rowStart: 0,
        pageSize: 1000,
      };
      const result = await fetchData(body);
      if (result) {
        setStates((result.items || []).map((state) => state.Value));
      }
    } catch (error) {
      console.error("Error fetching states:", error);
    }
  }, []);

  const fetchAllStates = useCallback(async () => {
    try {
      const body = {
        target: "listDropDownOptions",
        filterTerms: { Type: "State", Subtype: "India" },
        rowStart: 0,
        pageSize: 1000,
      };
      const result = await fetchData(body);
      if (result) setAllStates(result.items || []);
    } catch (error) {
      console.error("Error fetching all states:", error);
    }
  }, []);

  const fetchPaymentOptions = useCallback(async () => {
    try {
      const body = {
        target: "listDropDownOptions",
        filterTerms: { Type: "PaymentMode", Subtype: "India" },
        rowStart: 0,
        pageSize: 1000,
      };
      const result = await fetchData(body);
      if (result) setPaymentOptions(result.items || []);
    } catch (error) {
      console.error("Error fetching payment options:", error);
    }
  }, []);

  const handleSearchFilter = (searchValues) => {
    setFilterTerms(searchValues);
    setCurrentSellerItemDetailPage(1);
  };

  const handleAddClick = () => {
    setFormData({
      ProductID: "",
      Quantity: "",
      PricePerMT: "",
      State: "",
      Country: "India",
      Expires: "",
      Currency: "INR",
      TotalPrice: "",
      Payment: "",
      HsCode: "",
      Variety: "",
      productSpecifications: "",
    });
    setSelectedProductName("");
    setSelectedGrade("");
    setSelectedPaymentOption("");
    setSelectedState("");
    setIsEditMode(false);
    setActiveTab("commodityDetails");
    setShowAddForm(true);
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setIsEditMode(false);
    setFormData({});
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFormProductChange = (value) => {
    setSelectedProductName(value);
    const product = products.find((p) => p.ProductName === value);
    if (product) {
      setSelectedGrade(product.Grade);
      // Auto-fill HsCode and Variety from product
      setFormData((prev) => ({
        ...prev,
        HsCode: product.HsCode || "",
        Variety: product.Variety || "",
      }));
    }
  };

  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  const handleSubmit = async () => {
    const product = products.find(
      (p) => p.ProductName === selectedProductName && p.Grade === selectedGrade
    );
    if (!product) {
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Please select a valid product and grade",
        autoClose: false,
      });
      return;
    }

    setLoading(true);
    try {
      const itemData = {
        ProductID: product.ProductID,
        Quantity: parseFloat(formData.Quantity) || 0,
        PricePerMT: parseFloat(formData.PricePerMT) || 0,
        Price: parseFloat(formData.PricePerMT) || 0,
        State: selectedState,
        Country: formData.Country,
        Expires: formData.Expires,
        Currency: formData.Currency,
        TotalPrice: parseFloat(formData.TotalPrice) || 0,
        Payment: selectedPaymentOption,
        productSpecifications: formData.productSpecifications || "",
      };

      // Add SellerItemID for edit mode
      if (isEditMode && selectedSellerItemDetail) {
        itemData.SellerItemID = selectedSellerItemDetail.SellerItemID;
      }

      const body = {
        target: isEditMode ? "saveSellerItem" : "addSellerItem",
        SellerItemExtendedInput: itemData,
      };

      const result = await postQuery(body, "backendops");
      if (result) {
        setModal({
          show: true,
          type: "success",
          title: "Success",
          message: `Produce ${isEditMode ? "updated" : "added"} successfully!`,
          autoClose: false,
        });
        setShowAddForm(false);
        setIsEditMode(false);
        setFormData({});
        fetchSellerItemDetails();
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "adding"} produce: ${JSON.stringify(
          error
        )}`
      );
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: `Error ${
          isEditMode ? "updating" : "adding"
        } produce: ${JSON.stringify(error)}`,
        autoClose: false,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_SELLERS_AND_FARMERS")) {
          await Auth.signOut();
          navigate("/login/seller-farmer");
        }
      } catch (error) {
        await Auth.signOut();
        navigate("/login/seller-farmer");
      }
    };

    checkUser();
    fetchProducts();
    fetchStates();
    fetchAllStates();
    fetchPaymentOptions();
  }, [
    navigate,
    fetchProducts,
    fetchStates,
    fetchAllStates,
    fetchPaymentOptions,
  ]);

  useEffect(() => {
    fetchSellerItemDetails();
  }, [fetchSellerItemDetails]);

  // Auto-calculate Total Price when Quantity or Price changes
  useEffect(() => {
    const quantity = parseFloat(formData.Quantity) || 0;
    const price = parseFloat(formData.PricePerMT) || 0;
    const totalPrice = quantity * price;
    const currentTotal = parseFloat(formData.TotalPrice) || 0;

    // Only update if there's a meaningful change (avoid floating point precision issues)
    if (totalPrice > 0 && Math.abs(totalPrice - currentTotal) > 0.01) {
      setFormData((prev) => ({
        ...prev,
        TotalPrice: totalPrice.toFixed(2),
      }));
    } else if (totalPrice === 0 && currentTotal !== 0) {
      setFormData((prev) => ({
        ...prev,
        TotalPrice: "",
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.Quantity, formData.PricePerMT]);

  return (
    <div className="compare-offer-container">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />

      {/* Add Button at top right */}
      <div
        className="head-btn"
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          alignItems: "end",
          marginRight: "12px",
        }}
      >
        <div
          className="submit-btn"
          onClick={() => {
            if (!showAddForm) {
              handleAddClick();
            } else {
              handleCancel();
            }
          }}
        >
          {showAddForm ? "Cancel" : "Add"}
        </div>
      </div>

      {/* Search Filter and Title */}
      <div className="search-filter4">
        <div className="title4">
          <div className="head-text">Unapproved Produce</div>
        </div>
        <div className="filter4">
          <SearchFilter fields={fields} onSearch={handleSearchFilter} />
        </div>
      </div>

      {/* Data Table */}
      <div className="row main-row">
        <section className="offers">
          {loading && <div className="loading">Loading...</div>}
          {!loading && sellerItemDetails.length === 0 && (
            <div className="empty-state">No produce items found</div>
          )}
          {!loading && sellerItemDetails.length > 0 && (
            <DataTable
              columns={columns}
              data={sellerItemDetails}
              total={totalSellerItemDetailPages * pageSize}
              title="Produce Items"
              rowsPerPage={pageSize}
              onPageChange={handlePageChange}
              onRowClick={handleRowClick}
              currentPage={currentPage}
              selectedRows={
                selectedSellerItemDetail
                  ? [
                      selectedSellerItemDetail.SellerItemID ||
                        selectedSellerItemDetail.id,
                    ]
                  : []
              }
            />
          )}
        </section>
      </div>

      {/* Add Form - Below the table */}
      {showAddForm && (
        <div className="row">
          <section className="product-details-offers">
            {/* Submit and Cancel buttons positioned above tabs */}
            <div className="row" style={{ marginBottom: "20px" }}>
              <div className="col-lg-12 text-end">
                <button
                  type="button"
                  className="submit-btn"
                  onClick={handleCancel}
                  disabled={loading}
                >
                  CANCEL
                </button>
                <button
                  type="button"
                  className="submit-btn"
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{ marginLeft: "5px" }}
                >
                  {loading
                    ? isEditMode
                      ? "UPDATING..."
                      : "SUBMITTING..."
                    : isEditMode
                    ? "UPDATE"
                    : "SUBMIT"}
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="tabs">
              <button
                className={activeTab === "commodityDetails" ? "active" : ""}
                onClick={() => handleTabClick("commodityDetails")}
              >
                Commodity Details
              </button>
              <button
                className={activeTab === "specifications" ? "active" : ""}
                onClick={() => handleTabClick("specifications")}
              >
                Specifications
              </button>
              <button
                className={activeTab === "pricing" ? "active" : ""}
                onClick={() => handleTabClick("pricing")}
              >
                Pricing/Guarantee
              </button>
            </div>

            {/* Commodity Details Tab */}
            {activeTab === "commodityDetails" && (
              <div className="tab-content active">
                <div className="row g-3">
                  <div className="col-lg-4 col-md-6 col-12">
                    <label>Product *</label>
                    <select
                      className="form-control form-select"
                      value={selectedProductName}
                      onChange={(e) => handleFormProductChange(e.target.value)}
                    >
                      <option value="">Select Product</option>
                      {[...new Set(products.map((p) => p.ProductName))].map(
                        (product) => (
                          <option key={product} value={product}>
                            {product}
                          </option>
                        )
                      )}
                    </select>
                  </div>
                  <div className="col-lg-4 col-md-6 col-12">
                    <label>Grade / Quality *</label>
                    <select
                      className="form-control form-select"
                      value={selectedGrade}
                      onChange={(e) => setSelectedGrade(e.target.value)}
                    >
                      <option value="">Select Grade</option>
                      {products
                        .filter((p) => p.ProductName === selectedProductName)
                        .map((product) => (
                          <option key={product.Grade} value={product.Grade}>
                            {product.Grade}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="col-lg-4 col-md-6 col-12">
                    <label>HS Code</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="HsCode"
                      value={formData.HsCode || ""}
                      onChange={(e) =>
                        handleFormChange("HsCode", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-lg-4 col-md-6 col-12">
                    <label>Variety</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Variety"
                      value={formData.Variety || ""}
                      onChange={(e) =>
                        handleFormChange("Variety", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-lg-4 col-md-6 col-12">
                    <label>Origin Country</label>
                    <select
                      className="form-control form-select"
                      value={formData.Country || "India"}
                      onChange={(e) =>
                        handleFormChange("Country", e.target.value)
                      }
                    >
                      <option value="India">India</option>
                    </select>
                  </div>
                  <div className="col-lg-4 col-md-6 col-12">
                    <label>State</label>
                    <select
                      className="form-control form-select"
                      value={selectedState}
                      onChange={(e) => setSelectedState(e.target.value)}
                    >
                      <option value="">Select State</option>
                      {allStates.map((state) => (
                        <option key={state.Value} value={state.Value}>
                          {state.Value}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-lg-4 col-md-6 col-12">
                    <label>Quantity (MT)</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Enter Quantity"
                      value={formData.Quantity || ""}
                      onChange={(e) =>
                        handleFormChange("Quantity", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-lg-4 col-md-6 col-12">
                    <label>Expiry Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={formData.Expires || ""}
                      onChange={(e) =>
                        handleFormChange("Expires", e.target.value)
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Specifications Tab */}
            {activeTab === "specifications" && (
              <div className="tab-content active">
                <div className="row g-3">
                  <div className="col-12">
                    <label>Product Specifications</label>
                    <textarea
                      className="form-control"
                      rows="6"
                      maxLength="4000"
                      placeholder="Enter product specifications..."
                      value={formData.productSpecifications || ""}
                      onChange={(e) =>
                        handleFormChange(
                          "productSpecifications",
                          e.target.value
                        )
                      }
                      style={{
                        minHeight: "200px",
                        resize: "vertical",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Pricing Tab */}
            {activeTab === "pricing" && (
              <div className="tab-content active">
                <div className="row g-3">
                  <div className="col-lg-4 col-md-6 col-12">
                    <label>Currency</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.Currency || "INR"}
                      readOnly
                    />
                  </div>
                  <div className="col-lg-4 col-md-6 col-12">
                    <label>Price / MT</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Enter Price"
                      value={formData.PricePerMT || ""}
                      onChange={(e) =>
                        handleFormChange("PricePerMT", e.target.value)
                      }
                    />
                  </div>
                  <div className="col-lg-4 col-md-6 col-12">
                    <label>Total Price</label>
                    <input
                      type="number"
                      className="form-control"
                      placeholder="Auto-calculated"
                      value={formData.TotalPrice || ""}
                      readOnly
                      style={{ backgroundColor: "#f0f0f0" }}
                    />
                  </div>
                  <div className="col-lg-4 col-md-6 col-12">
                    <label>Payment Method</label>
                    <select
                      className="form-control form-select"
                      value={selectedPaymentOption}
                      onChange={(e) => setSelectedPaymentOption(e.target.value)}
                    >
                      <option value="">Select Payment Method</option>
                      {paymentOptions.map((option) => (
                        <option key={option.Value} value={option.Value}>
                          {option.Value}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

export default UnapprovedProduce;
